<!--
  Gestion des complementaires (saisie manuelle du contrat complementaire).
  Port web de fcompmanuel.vb (frmcompmanuel) : ouvert en popup depuis le bouton
  « Part Complementaire » de NewFSE.vue.

  - Deux onglets : Gestion unique (routage RO) / Gestion separee (routage AMC).
  - Lecture/ecriture directes sur facfse.Ident.Cc via src/script/Complementaire.js.
  - Composant autonome : lit le facfse du store et pousse via setFacfse, comme
    le panneau de detail d'acte (appliquerDetail) de NewFSE.vue.
-->
<template>
    <v-card class="gestion-comp">
        <v-card-title class="d-flex align-center">
            <span>Gestion des complementaires</span>
            <v-spacer></v-spacer>
            <v-btn icon="mdi-close" size="small" variant="text" @click="annuler"></v-btn>
        </v-card-title>

        <!-- Onglets : mode de gestion -->
        <v-tabs v-model="ongletMode" color="#119393" density="compact">
            <v-tab :value="MODE_UNIQUE">Gestion unique (RO)</v-tab>
            <v-tab :value="MODE_SEPAREE">Gestion separee</v-tab>
        </v-tabs>

        <v-card-text>
            <!-- Bouton recuperation des donnees patient (Command4 du legacy) -->
            <div class="d-flex mb-3">
                <v-btn size="small" variant="outlined" color="#119393"
                       prepend-icon="mdi-account-arrow-down"
                       @click="recupererDonneesPatient">
                    Recuperer les donnees patient
                </v-btn>
                <v-btn size="small" variant="outlined" color="#119393" class="ml-2"
                       prepend-icon="mdi-qrcode-scan" @click="demarrerCaptureQr">
                    Lire QR mutuelle
                </v-btn>
                <v-btn v-if="!estUnique" size="small" variant="outlined" color="#119393" class="ml-2"
                       prepend-icon="mdi-cloud-search-outline"
                       :loading="chargementAnnuaire" @click="interrogerAnnuaire">
                    Interroger l'annuaire AMC
                </v-btn>
                <v-spacer></v-spacer>
            </div>

            <!-- Resultats du teleservice Annuaire AMC. Les champs sont bruts (cf
                 AnnuaireAMC.js) : routage/domaine/hote B2 ne sont PAS garantis par
                 la reponse annuaire -> l'utilisateur valide en cliquant. -->
            <div v-if="resultatsAnnuaire.length" class="resultats-annuaire mb-3">
                <div class="ra-titre">Resultats annuaire ({{ resultatsAnnuaire.length }}) - cliquez pour appliquer</div>
                <div v-for="(r, i) in resultatsAnnuaire" :key="i" class="ra-ligne" @click="choisirResultatAnnuaire(r, i)">
                    <div class="ra-conv">{{ raChamp(r, 'TypeConvention', 'typeConvention') || '-' }}</div>
                    <div class="ra-detail">
                        <span>routage: {{ raChamp(r, 'Routage', 'routage') || '-' }}</span>
                        <span>domaine: {{ raChamp(r, 'Domaine', 'domaine') || '-' }}</span>
                        <span>hote: {{ raChamp(r, 'Hote', 'hote') || '-' }}</span>
                        <span v-if="raChamp(r, 'Libelle', 'libelle')">{{ raChamp(r, 'Libelle', 'libelle') }}</span>
                    </div>
                    <div v-if="raChamp(r, 'Message', 'message')" class="ra-message">{{ raChamp(r, 'Message', 'message') }}</div>
                </div>
            </div>

            <div class="grille-comp">
                <!-- Champs communs aux deux modes -->
                <div class="champ taille-md">
                    <label>Numero de contrat</label>
                    <input v-model="comp.contrat" maxlength="10" class="champ-input"
                           @input="comp.contrat = comp.contrat.toUpperCase()" />
                </div>
                <div class="champ taille-sm">
                    <label>N. adherent</label>
                    <input v-model="comp.noAdherent" maxlength="8" class="champ-input" />
                </div>
                <div class="champ taille-md">
                    <label>Identifiant assure AMC</label>
                    <input v-model="comp.noAssureAMC" maxlength="15" class="champ-input" />
                </div>

                <!-- Gestion unique : numero AMC -->
                <div v-if="estUnique" class="champ taille-sm">
                    <label>Numero AMC</label>
                    <input v-model="comp.numeroAMC" maxlength="10" class="champ-input" />
                </div>

                <!-- Gestion separee : routage / domaine / hote / type / critere -->
                <div v-if="!estUnique" class="champ taille-sm">
                    <label>Routage</label>
                    <input v-model="comp.routage" list="routages-comp" maxlength="2"
                           class="champ-input"
                           @input="comp.routage = comp.routage.toUpperCase()" />
                    <datalist id="routages-comp">
                        <option v-for="r in routages" :key="r.valeur" :value="r.valeur">{{ r.libelle }}</option>
                    </datalist>
                </div>
                <div v-if="!estUnique" class="champ taille-md">
                    <label>Domaine</label>
                    <input v-model="comp.domaine" maxlength="20" class="champ-input" />
                </div>
                <div v-if="!estUnique" class="champ taille-xs">
                    <label>Hote</label>
                    <input v-model="comp.hote" maxlength="3" class="champ-input"
                           @blur="comp.hote = formatHote(comp.hote)" />
                </div>
                <div v-if="!estUnique" class="champ taille-xs">
                    <label>Type convention</label>
                    <input v-model="comp.typeConvention" maxlength="2" class="champ-input"
                           @input="comp.typeConvention = comp.typeConvention.toUpperCase()" />
                </div>
                <div v-if="!estUnique" class="champ taille-sm">
                    <label>Critere secondaire</label>
                    <input v-model="comp.critSec" maxlength="9" class="champ-input" />
                </div>

                <!-- Champs communs (suite) -->
                <div class="champ taille-xs">
                    <label>Indicateur traitement</label>
                    <input v-model="comp.indicateurTraitement" maxlength="2" class="champ-input"
                           @blur="comp.indicateurTraitement = formatIndicateur(comp.indicateurTraitement)" />
                </div>
                <div class="champ taille-sm">
                    <label>Code STS</label>
                    <input v-model="comp.codeSTS" maxlength="10" class="champ-input"
                           @input="comp.codeSTS = comp.codeSTS.toUpperCase()" />
                </div>

                <!-- Dates de validite (informatif) -->
                <div class="champ taille-sm">
                    <label>Debut validite</label>
                    <input :value="comp.debutValidite" maxlength="10" placeholder="jj/mm/aaaa"
                           class="champ-input champ-date"
                           :class="{ 'champ-date-invalide': !dateValide(comp.debutValidite) }"
                           @input="onSaisieDate('debutValidite', $event)" />
                </div>
                <div class="champ taille-sm">
                    <label>Fin validite</label>
                    <input :value="comp.finValidite" maxlength="10" placeholder="jj/mm/aaaa"
                           class="champ-input champ-date"
                           :class="{ 'champ-date-invalide': !dateValide(comp.finValidite) }"
                           @input="onSaisieDate('finValidite', $event)" />
                </div>
            </div>

            <!-- Cases a cocher -->
            <div class="d-flex flex-wrap ga-4 mt-2">
                <v-checkbox v-model="comp.attestationPresentee" label="Attestation presentee"
                            density="compact" hide-details></v-checkbox>
                <v-checkbox v-model="comp.priseEnCharge" label="Prise en charge"
                            density="compact" hide-details></v-checkbox>
                <v-checkbox v-if="!estUnique" v-model="comp.nonAccordAMO"
                            label="Reponse defavorable accord AMO" density="compact" hide-details></v-checkbox>
                <v-checkbox v-if="!estUnique" v-model="comp.horsTP" label="Hors tiers payant"
                            density="compact" hide-details></v-checkbox>
            </div>

            <!-- Erreurs de validation -->
            <div v-if="erreurs.length" class="erreurs-comp mt-3">
                <div v-for="(e, i) in erreurs" :key="i">{{ e }}</div>
            </div>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="annuler">Annuler</v-btn>
            <v-btn color="#119393" :loading="enregistrement" @click="valider">Valider</v-btn>
        </v-card-actions>

        <!-- Overlay de capture du code 2D mutuelle (emulation clavier douchette) -->
        <v-overlay v-model="captureQr" class="align-center justify-center" scrim="#000">
            <v-card class="pa-4" min-width="320">
                <div class="mb-2" style="font-weight:bold;">Scannez l'attestation mutuelle...</div>
                <input ref="inputQr" class="champ-input" style="width:100%"
                       @keydown="onQrKeydown" @blur="focusQr" />
                <div class="text-caption mt-2">Ou saisissez le code puis Entree.</div>
                <div class="d-flex justify-end mt-2">
                    <v-btn size="small" variant="text" @click="annulerCaptureQr">Annuler</v-btn>
                </div>
            </v-card>
        </v-overlay>
    </v-card>
</template>

<script>
    import { defineComponent } from 'vue';
    import {
        MODE_UNIQUE, MODE_SEPAREE, ROUTAGES,
        complementaireVide, complementaireDepuisFacfse,
        appliquerComplementaireDansFacfse, validerComplementaire,
        validerComplementaireBackend,
        dateValide, formatIndicateur, formatHote,
    } from '../script/Complementaire.js';
    import { appelAnnuaireAMC } from '../script/AnnuaireAMC.js';
    import { parseAMC, traduireCodeBarre, getTypeConvPourSpecialite } from '../script/QrCodeAMC.js';

    export default defineComponent({
        emits: ['close'],

        data() {
            return {
                MODE_UNIQUE, MODE_SEPAREE,
                routages: ROUTAGES,
                comp: complementaireVide(),
                ongletMode: MODE_UNIQUE,
                erreurs: [],
                enregistrement: false,
                captureQr: false,
                tamponQr: '',
                timerQr: null,
                resultatsAnnuaire: [],
                chargementAnnuaire: false,
                // true = la liste resultatsAnnuaire affichee provient d'une VALIDATION (choix requis
                // cote serveur) -> le clic sur un resultat relance la validation avec l'index choisi ;
                // false = liste issue du bouton "Interroger l'annuaire" -> le clic remplit juste le form.
                choixAnnuairePourValidation: false,
            };
        },

        computed: {
            estUnique() { return this.ongletMode === MODE_UNIQUE; },
            // AMC scannee cote natif, mise en attente par App.vue (store FSEHandler).
            amcEnAttenteStore() { return this.$store.getters['FSEHandler/getAmcEnAttente']; },
        },

        watch: {
            // L'onglet pilote le mode de gestion du modele (cf Tabstrip1_Click).
            ongletMode(val) { this.comp.modeGestion = val; },
            // Une AMC arrive alors que l'ecran est deja ouvert -> on remplit direct.
            amcEnAttenteStore(val) { if (val) this.consommerAmcEnAttente(); },
        },

        methods: {
            dateValide, formatIndicateur, formatHote,

            // Applique une AMC scannee (store) au formulaire, puis interroge
            // l'annuaire AMC (best-effort) pour proposer routage/domaine/hote
            // (champs B2 obligatoires absents du QR). On vide ensuite l'attente.
            consommerAmcEnAttente() {
                const infos = this.$store.getters['FSEHandler/getAmcEnAttente'];
                if (!infos || !infos.numeroAMC) return;
                this.appliquerInfosAMC(infos);
                try { this.interrogerAnnuaire(); } catch (e) { console.error(e); }
                this.$store.commit('FSEHandler/setAmcEnAttente', null);
            },

            // Lit un champ du resultat annuaire en tolerant la casse backend
            // (PascalCase 1re lettre : Routage/Domaine...) ou camelCase.
            raChamp(r, ...cles) {
                if (!r) return '';
                for (const c of cles) {
                    if (r[c] !== undefined && r[c] !== null && r[c] !== '') return r[c];
                }
                return '';
            },

            // Interroge le teleservice Annuaire AMC via le backend. En gestion
            // separee : criteres = numero AMC (contrat), type convention, CSR.
            async interrogerAnnuaire() {
                this.erreurs = [];
                this.resultatsAnnuaire = [];
                const numeroAMC = (this.comp.contrat || '').trim();
                if (!numeroAMC) {
                    this.erreurs = ['Renseignez le numero AMC (contrat) avant d\'interroger l\'annuaire.'];
                    return;
                }
                this.chargementAnnuaire = true;
                try {
                    const req = {
                        numeroAMC,
                        typeConvention: this.comp.typeConvention || '',
                        csr: this.comp.critSec || '',
                        domaineConventionnel: '', // TODO : domaine PS courant si dispo
                        horsTP: !!this.comp.horsTP,
                    };
                    const rep = await appelAnnuaireAMC(req);
                    const liste = Array.isArray(rep) ? rep : (rep && rep.data) || [];
                    this.resultatsAnnuaire = liste;
                    if (!liste.length) {
                        this.erreurs = ['Annuaire AMC : aucun resultat.'];
                    }
                } catch (e) {
                    console.error('Erreur appel annuaire AMC :', e);
                    this.erreurs = ['Erreur lors de l\'appel a l\'annuaire AMC (voir console / certificat IGC-Sante).'];
                } finally {
                    this.chargementAnnuaire = false;
                }
            },

            // Applique un resultat annuaire choisi. On remplit ce qui est fiable
            // (type convention) et on propose routage/domaine/hote renvoyes. Si la liste
            // provenait d'une VALIDATION (choix requis cote serveur), on relance la validation
            // avec l'index choisi (2e passe : appelAnnuaire2 headless prend resultats[i]).
            async choisirResultatAnnuaire(r, i) {
                const typeConv = this.raChamp(r, 'TypeConvention', 'typeConvention');
                const routage = this.raChamp(r, 'Routage', 'routage');
                const domaine = this.raChamp(r, 'Domaine', 'domaine');
                const hote = this.raChamp(r, 'Hote', 'hote');
                if (typeConv) this.comp.typeConvention = typeConv;
                if (routage) this.comp.routage = routage;
                if (domaine) this.comp.domaine = domaine;
                if (hote) this.comp.hote = hote;
                const modeValidation = this.choixAnnuairePourValidation;
                this.resultatsAnnuaire = [];
                this.choixAnnuairePourValidation = false;
                if (modeValidation) {
                    await this.soumettreValidation(i);
                }
            },

            // Formate progressivement une saisie de date en jj/mm/aaaa.
            formaterDateSaisie(valeur) {
                const chiffres = String(valeur || '').replace(/\D/g, '').slice(0, 8);
                let out = chiffres.slice(0, 2);
                if (chiffres.length >= 3) out += '/' + chiffres.slice(2, 4);
                if (chiffres.length >= 5) out += '/' + chiffres.slice(4, 8);
                return out;
            },
            onSaisieDate(cle, evt) {
                const formate = this.formaterDateSaisie(evt.target.value);
                this.comp[cle] = formate;
                evt.target.value = formate;
            },

            // Pre-remplit le formulaire depuis les donnees deja portees par le
            // facfse (Command4 du legacy : get_donnees_complementaire_base_patient).
            // Pas d'endpoint dedie cote web -> on relit le contrat present sur le
            // facfse. Best-effort : si rien, on laisse le formulaire en l'etat.
            recupererDonneesPatient() {
                const facfse = this.$store.getters['FSEHandler/getFacfse'];
                const charge = complementaireDepuisFacfse(facfse);
                // ne remplace que si on a au moins un contrat ou un numero AMC
                if (charge.contrat || charge.numeroAMC) {
                    this.comp = charge;
                    this.ongletMode = charge.modeGestion === MODE_SEPAREE ? MODE_SEPAREE : MODE_UNIQUE;
                } else {
                    this.erreurs = ['Aucune donnee complementaire disponible sur la facture.'];
                }
            },

            // -----------------------------------------------------------------
            // Lecture du code 2D mutuelle par emulation clavier (douchette USB).
            // La douchette tape dans le champ focus ; on accumule la frappe puis
            // on la de-brouille via traduireCodeBarre (port du legacy LireQrCode).
            // -----------------------------------------------------------------
            demarrerCaptureQr() {
                this.tamponQr = '';
                this.erreurs = [];
                this.captureQr = true;
                this.$nextTick(() => this.focusQr());
            },
            focusQr() {
                if (this.captureQr) {
                    this.$nextTick(() => { if (this.$refs.inputQr) this.$refs.inputQr.focus(); });
                }
            },
            annulerCaptureQr() {
                this.captureQr = false;
                this.tamponQr = '';
                if (this.timerQr) { clearTimeout(this.timerQr); this.timerQr = null; }
            },
            onQrKeydown(evt) {
                // Terminateur : '/' (comme le legacy qui attend le '/') ou Entree.
                if (evt.key === 'Enter' || evt.key === '/') {
                    evt.preventDefault();
                    const brut = this.tamponQr + (evt.key === '/' ? '/' : '');
                    this.finaliserQr(brut);
                    return;
                }
                // On n'accumule que les caracteres imprimables (ignore Shift, Ctrl, Tab, fleches...)
                if (evt.key.length === 1) {
                    this.tamponQr += evt.key;
                }
                // Garde : si le scan se bloque, on abandonne apres 3s d'inactivite.
                if (this.timerQr) clearTimeout(this.timerQr);
                this.timerQr = setTimeout(() => this.annulerCaptureQr(), 3000);
            },
            finaliserQr(brut) {
                this.annulerCaptureQr();
                // Fallback legacy (LireQrCode/LireTexteQrCode) : traduire le brut ; si la
                // traduction ne contient pas '#' ET '/', l'entree etait deja propre -> brut.
                const traduit = traduireCodeBarre(brut);
                const texte = (traduit.indexOf('#') !== -1 && traduit.indexOf('/') !== -1) ? traduit : brut;
                const infos = parseAMC(texte);
                if (!infos.numeroAMC) {
                    this.erreurs = ['Code 2D mutuelle illisible ou format inattendu.'];
                    return;
                }
                this.appliquerInfosAMC(infos);
            },
            // Port de retour_qr_code (fcompmanuel.vb l.461-487).
            appliquerInfosAMC(infos) {
                const domainePS = ''; // TODO : specialite PS courante si dispo. Sinon type brut.
                const typeConv = getTypeConvPourSpecialite(infos.typeConvention, domainePS);
                // RO/GU -> gestion unique, sinon gestion separee.
                this.ongletMode = (typeConv === 'RO' || typeConv === 'GU') ? MODE_UNIQUE : MODE_SEPAREE;
                this.comp.contrat = infos.numeroAMC;
                this.comp.noAdherent = infos.numeroAdherent;
                this.comp.critSec = infos.csr;
                this.comp.typeConvention = typeConv;
                this.comp.attestationPresentee = true;
                this.erreurs = [];
            },

            annuler() {
                this.$emit('close');
            },

            // Valide la saisie : controle local, puis push du facfse (avec la saisie
            // utilisateur) au serveur, puis VALIDATION METIER PARTAGEE cote backend
            // (valider_parametres_complementaires_manuels), et enfin rafraichissement
            // du facfse local (le serveur a raffine Ident.Cc). Copie defensive du facfse
            // (comme appliquerDetail de NewFSE.vue) pour ne pas muter l'etat Vuex en place.
            async valider() {
                this.comp.modeGestion = this.ongletMode;
                const { ok, erreurs } = validerComplementaire(this.comp);
                this.erreurs = erreurs;
                if (!ok) return;
                await this.soumettreValidation(-1);
            },

            // Pousse le facfse (saisie) au serveur, lance la validation metier PARTAGEE, puis selon
            // le retour : choix annuaire requis -> affiche la liste des conventions (l'utilisateur
            // clique -> 2e passe via choisirResultatAnnuaire) ; succes -> rafraichit le facfse (cc
            // raffine par recherche_OC) et ferme ; echec -> message (erreurs metier aussi en popup
            // GET /Erreur). choixAnnuaireIndex : -1 (1re passe) ou index choisi (2e passe).
            async soumettreValidation(choixAnnuaireIndex) {
                this.enregistrement = true;
                try {
                    const original = this.$store.getters['FSEHandler/getFacfse'];
                    if (!original) {
                        this.erreurs = ['Aucune facture chargee (facfse absent).'];
                        return;
                    }
                    // 1) Ecrit la saisie dans une copie du facfse et la pousse au serveur,
                    //    pour que la validation metier travaille sur ce contexte.
                    const facfse = JSON.parse(JSON.stringify(original));
                    const cc = appliquerComplementaireDansFacfse(facfse, this.comp);
                    if (!cc) {
                        this.erreurs = ['Structure Ident.Cc introuvable dans le facfse.'];
                        return;
                    }
                    await this.$store.dispatch('FSEHandler/setFacfse', facfse);

                    // 2) Validation metier PARTAGEE avec le desktop (backend).
                    const rep = await validerComplementaireBackend(this.comp, choixAnnuaireIndex);

                    // 2a) L'annuaire a renvoye plusieurs conventions : on presente la liste (UI
                    //     resultatsAnnuaire) et on attend le choix (choisirResultatAnnuaire -> 2e passe).
                    if (rep && rep.choixAnnuaireRequis) {
                        this.resultatsAnnuaire = rep.resultatsAnnuaire || [];
                        this.choixAnnuairePourValidation = true;
                        this.erreurs = rep.message ? [rep.message] : ['Choisissez une convention complementaire.'];
                        return;
                    }

                    // 2b) Echec metier (les erreurs remontent aussi en popup GET /Erreur).
                    if (!rep || !rep.ok) {
                        if (rep && rep.miseAJourContratComplementaire) {
                            this.erreurs = ['Contrat inconnu dans la table des conventions : creez-le dans la gestion des conventions (voir message affiche).'];
                        } else {
                            this.erreurs = ['La validation de la complementaire a echoue (voir le message affiche).'];
                        }
                        return; // on ne ferme pas : l'utilisateur corrige
                    }

                    // 3) Succes : le serveur a raffine facfse.Ident.Cc (domaine/hote/indicateur/
                    //    piece justificative via la table mutuelles). On rafraichit le facfse local.
                    await this.$store.dispatch('FSEHandler/fetchFacfse');
                    this.$emit('close');
                } catch (e) {
                    console.error('Erreur lors de la validation du complementaire :', e);
                    this.erreurs = ['Erreur cote serveur lors de la validation de la complementaire.'];
                } finally {
                    this.enregistrement = false;
                }
            },
        },

        mounted() {
            // Pre-charge le contrat existant du facfse au cas ou.
            const facfse = this.$store.getters['FSEHandler/getFacfse'];
            const charge = complementaireDepuisFacfse(facfse);
            this.comp = charge;
            this.ongletMode = charge.modeGestion === MODE_SEPAREE ? MODE_SEPAREE : MODE_UNIQUE;
            // Ouverture pre-remplie suite a un scan AMC (natif) : on applique l'AMC
            // en attente par-dessus le pre-chargement.
            this.consommerAmcEnAttente();
        },
    });
</script>

<style scoped>
    * { color: #434343; }

    .gestion-comp { padding-bottom: 6px; }

    /* Onglets : sur mobile le dialog est etroit ET haut -> le v-card atteint sa
       hauteur max et le flex ecrasait les onglets a hauteur 0 (invisibles, le
       contenu se superposait dessus). On empeche le shrink des onglets et on
       laisse le corps scroller. On repartit aussi les 2 onglets sur toute la
       largeur pour eviter le debordement horizontal.
       PAS de media query (le WebView MAUI les ignore en largeur, cf memoire). */
    .gestion-comp :deep(.v-tabs) { flex: 0 0 auto; }
    .gestion-comp :deep(.v-slide-group__content) { width: 100%; }
    .gestion-comp :deep(.v-tab) { flex: 1 1 0; min-width: 0; }
    .gestion-comp :deep(.v-card-text) { flex: 1 1 auto; min-height: 0; overflow-y: auto; }

    .grille-comp {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
    /* Champs fluides : grandissent pour remplir la ligne et passent a la ligne
       selon la largeur disponible (mobile compris), sans media query. */
    .champ {
        display: flex;
        flex-direction: column;
        flex: 1 1 160px;
        min-width: 130px;
    }
    .champ.taille-xs { flex-basis: 110px; }
    .champ.taille-sm { flex-basis: 160px; }
    .champ.taille-md { flex-basis: 220px; }

    .champ label {
        font-size: 0.8rem;
        font-weight: bold;
        color: #555;
        margin-bottom: 4px;
    }
    .champ-input {
        padding: 8px 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 0.95rem;
        outline: none;
    }
    .champ-input:focus { border-color: #119393; }
    .champ-date-invalide { border-color: #E53935 !important; color: #E53935; }

    .erreurs-comp {
        color: #E91E63;
        font-weight: bold;
        font-size: 0.9rem;
        line-height: 1.4;
    }

    .resultats-annuaire {
        border: 1px solid #119393;
        border-radius: 6px;
        overflow: hidden;
    }
    .resultats-annuaire .ra-titre {
        background: #119393;
        color: #fff;
        font-size: 0.8rem;
        font-weight: bold;
        padding: 4px 8px;
    }
    .resultats-annuaire .ra-ligne {
        padding: 6px 8px;
        border-top: 1px solid #e0e0e0;
        cursor: pointer;
    }
    .resultats-annuaire .ra-ligne:hover { background: #e6f4f4; }
    .resultats-annuaire .ra-conv { font-weight: bold; }
    .resultats-annuaire .ra-detail {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        font-size: 0.82rem;
        color: #555;
    }
    .resultats-annuaire .ra-message { font-size: 0.8rem; color: #E91E63; }
</style>
